import { z } from "zod";

import {
  OpaqueIdSchema,
  PasswordSchema,
  PlatformRoleSchema,
  UserSummarySchema,
  UsernameSchema,
} from "./identity-schemas";

/**
 * Which kind of directory owns an account. `saml` covers every SAML 2.0 provider through one
 * provider-neutral flow (ADR-0059); the specific vendor is a per-connection presentation detail,
 * not an identity namespace.
 */
export const DirectoryProviderSchema = z.enum(["google", "saml"]);

export const DirectoryEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, "Enter the person’s directory address.")
  .max(320, "Keep the address under 320 characters.")
  .refine((value) => z.email().safeParse(value).success, "Enter a valid directory email address.");

/**
 * Where a user's name, sign-in address, and credential are controlled.
 *
 * `portal` — the platform owns them and an administrator may change them here.
 * `directory` — an enterprise identity provider owns them and the portal must not overwrite them.
 */
export const IdentitySourceSchema = z.enum(["portal", "directory"]);

/**
 * The address an invitation is sent to, which becomes the name that person signs in with.
 *
 * Two limits beyond "is an email" earn their place. It is capped at 64 characters because that is
 * the width of `username_normalized` and its CHECK constraint. And it is restricted to the
 * `UsernameSchema` character set, because the sign-in form parses what it is given with that
 * schema — an address containing `+` would be accepted here and then refused at login, leaving an
 * account nobody can reach.
 */
export const InviteEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, "Enter the person’s email address.")
  .max(64, "Keep the address under 64 characters.")
  .refine((value) => z.email().safeParse(value).success, "Enter a valid email address.")
  .refine(
    (value) => /^[a-z0-9._@-]+$/.test(value),
    "This address cannot be a sign-in name. Use one without “+” or other symbols.",
  );

/**
 * Who owns this member's role and tool access (ADR-0070).
 *
 * `portal` — an administrator sets them here.
 * `scim` — an identity provider's group mappings compute them, and the portal shows them read-only.
 *
 * Distinct from `IdentitySourceSchema`, which says who owns the *credential*. The two are
 * independent: a workspace may provision through SCIM while its people still sign in with a
 * password, and it may use SAML sign-on with no provisioning at all.
 */
export const MemberManagedBySchema = z.enum(["portal", "scim"]);

export const WorkspaceMemberIdentitySchema = z.strictObject({
  source: IdentitySourceSchema,
  provider: DirectoryProviderSchema.nullable(),
  /** Present only for a directory identity, so an administrator can see which account signs in. */
  email: DirectoryEmailSchema.nullable(),
  /** Null while an administrator has pre-registered the address but nobody has signed in yet. */
  linkedAt: z.number().int().positive().nullable(),
  managedBy: MemberManagedBySchema.default("portal"),
  /**
   * The mapped groups that produced this member's grant, named so an administrator looking at a
   * disabled control can see why it is disabled and where to change it. Empty for a provisioned
   * member whose groups are all unmapped — which grants nothing, and is the resting state.
   */
  managedByGroups: z.array(z.string().min(1).max(256)).max(50).default([]),
});

export const WorkspaceMemberStatusSchema = z.enum(["active", "suspended"]);

export const WorkspaceMemberSchema = z.strictObject({
  membershipId: OpaqueIdSchema,
  user: UserSummarySchema,
  role: PlatformRoleSchema,
  toolAccess: z.array(OpaqueIdSchema).max(100),
  status: WorkspaceMemberStatusSchema,
  suspendedAt: z.number().int().positive().nullable(),
  identity: WorkspaceMemberIdentitySchema,
});

export const WorkspaceMemberRoleSchema = z.enum(["organization_admin", "member"]);

const WorkspaceToolAccessSelectionSchema = z
  .array(OpaqueIdSchema)
  .max(100)
  .refine(
    (toolAccess) => new Set(toolAccess).size === toolAccess.length,
    "Choose each tool only once.",
  );

export const WorkspaceMemberAccessUpdateSchema = z.strictObject({
  toolAccess: WorkspaceToolAccessSelectionSchema,
});

export const WorkspaceInvitationKindSchema = z.enum(["portal", "directory"]);
export const WorkspaceInvitationStatusSchema = z.enum([
  "pending",
  "accepted",
  "revoked",
  "expired",
]);

export const WorkspaceInvitationSchema = z.strictObject({
  invitationId: OpaqueIdSchema,
  kind: WorkspaceInvitationKindSchema,
  username: UsernameSchema,
  displayName: z.string().min(1).max(120),
  directoryProvider: DirectoryProviderSchema.nullable(),
  directoryEmail: DirectoryEmailSchema.nullable(),
  role: WorkspaceMemberRoleSchema,
  toolAccess: z.array(OpaqueIdSchema).max(100),
  status: WorkspaceInvitationStatusSchema,
  expiresAt: z.number().int().positive(),
  createdAt: z.number().int().positive(),
});

/**
 * One address, used for everything (ADR-0062).
 *
 * A directory invitation used to carry a second `directoryEmail` field beside this one, so an
 * administrator could enter a sign-in name and a directory address that disagreed. The address a
 * person is invited at is now the address their directory asserts and the name they sign in with.
 */
export const WorkspaceInvitationCreateSchema = z
  .strictObject({
    kind: WorkspaceInvitationKindSchema,
    /** The address the claim link is emailed to, the sign-in name, and the directory address. */
    username: InviteEmailSchema,
    displayName: z
      .string()
      .trim()
      .min(2, "Enter the person’s name.")
      .max(120, "Keep the name under 120 characters."),
    role: WorkspaceMemberRoleSchema,
    toolAccess: WorkspaceToolAccessSelectionSchema,
  })
  .superRefine((input, context) => {
    if (input.role === "organization_admin" && input.toolAccess.length > 0) {
      context.addIssue({
        code: "custom",
        message: "Workspace administrators automatically receive every tool.",
        path: ["toolAccess"],
      });
    }
  });

export const InvitationClaimTokenSchema = z.string().min(32).max(256);

/**
 * What became of the invitation email. Delivery never gates the invitation itself: the claim link
 * is still returned and still works, so a mail outage costs the administrator a copy-and-paste
 * rather than the ability to onboard anyone.
 *
 * `skipped` covers an invitation with no link to send, which today means a directory invitation.
 */
export const InvitationEmailDeliverySchema = z.enum([
  "sent",
  "failed",
  "not_configured",
  "skipped",
]);

export const WorkspaceInvitationCreatedSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  invitation: WorkspaceInvitationSchema,
  /**
   * Returned exactly once, when a portal invitation is created. Only the hash is stored, so the
   * administrator must hand this link over now; it can never be read back.
   */
  claimToken: InvitationClaimTokenSchema.nullable(),
  emailDelivery: InvitationEmailDeliverySchema,
  updatedAt: z.number().int().positive(),
});

export const WorkspaceInvitationResultSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  invitation: WorkspaceInvitationSchema,
  updatedAt: z.number().int().positive(),
});

export const WorkspaceMemberRoleUpdateSchema = z.strictObject({
  role: WorkspaceMemberRoleSchema,
});

export const WorkspaceMemberStatusUpdateSchema = z.strictObject({
  status: WorkspaceMemberStatusSchema,
});

export const WorkspaceMemberResultSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  member: WorkspaceMemberSchema,
  updatedAt: z.number().int().positive(),
});

export const WorkspaceMemberRemovedSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  membershipId: OpaqueIdSchema,
  updatedAt: z.number().int().positive(),
});

export const InvitationLookupRequestSchema = z.strictObject({
  token: InvitationClaimTokenSchema,
});

/**
 * What an unauthenticated visitor holding a claim token may see. Deliberately narrow: the
 * workspace name and the name the administrator entered, and no roster, tool, or member detail.
 */
export const InvitationPreviewSchema = z.strictObject({
  organizationName: z.string().min(1).max(160),
  username: UsernameSchema,
  displayName: z.string().min(1).max(120),
  expiresAt: z.number().int().positive(),
});

export const InvitationAcceptRequestSchema = z.strictObject({
  token: InvitationClaimTokenSchema,
  password: PasswordSchema,
});

/**
 * Accepting an invitation deliberately does not sign the person in. They are sent to the sign-in
 * page with their username, which proves the password they just chose works before they rely on it.
 */
export const InvitationAcceptedSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  username: UsernameSchema,
  /**
   * Where this new account can actually sign in.
   *
   * Absolute, and resolved by the server, because an invitation is emailed from the neutral shell
   * while the workspace it invites somebody into may only accept sign-ins at its own address
   * (ADR-0063). A relative `/login` would land the person on the one host that is about to turn
   * them away, on their very first attempt.
   */
  signInUrl: z.url(),
});

export type WorkspaceMemberAccessUpdate = z.infer<typeof WorkspaceMemberAccessUpdateSchema>;
export type WorkspaceMember = z.infer<typeof WorkspaceMemberSchema>;
export type WorkspaceMemberIdentity = z.infer<typeof WorkspaceMemberIdentitySchema>;
export type WorkspaceMemberRoleUpdate = z.infer<typeof WorkspaceMemberRoleUpdateSchema>;
export type WorkspaceMemberStatusUpdate = z.infer<typeof WorkspaceMemberStatusUpdateSchema>;
export type WorkspaceMemberResult = z.infer<typeof WorkspaceMemberResultSchema>;
export type WorkspaceMemberRemoved = z.infer<typeof WorkspaceMemberRemovedSchema>;
export type WorkspaceInvitation = z.infer<typeof WorkspaceInvitationSchema>;
export type WorkspaceInvitationCreate = z.infer<typeof WorkspaceInvitationCreateSchema>;
export type WorkspaceInvitationCreated = z.infer<typeof WorkspaceInvitationCreatedSchema>;
export type InvitationEmailDelivery = z.infer<typeof InvitationEmailDeliverySchema>;
export type WorkspaceInvitationResult = z.infer<typeof WorkspaceInvitationResultSchema>;
export type InvitationLookupRequest = z.infer<typeof InvitationLookupRequestSchema>;
export type InvitationPreview = z.infer<typeof InvitationPreviewSchema>;
export type InvitationAcceptRequest = z.infer<typeof InvitationAcceptRequestSchema>;
export type InvitationAccepted = z.infer<typeof InvitationAcceptedSchema>;
